import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface WallSessionMeta {
  orderId?: string;
  printingService?: string;
  serverAddress?: string;
  /** Printer / branch serial_name where the order was sent */
  serialName?: string;
  /** Username used in express /uploads/{user}/ thumbnail URLs */
  userName?: string;
}

/**
 * Single printed page to open like "share from unshared" (edit focus → confirm),
 * instead of the multi-page draft picker.
 */
export interface WallPendingDirectShare {
  id: string;
  fileId: string;
  printingService: 'plotter' | 'express';
  page: number;
  serialName: string;
  fileName?: string;
  thumbUrl: string;
  serverAddress?: string;
  orderId?: string;
}

/** Per-page draft fields kept while the user leaves the wall without publishing. */
export interface WallSessionDraftPageState {
  fileId: string;
  page: number;
  included?: boolean;
  title?: string;
  description?: string;
  rotation?: 0 | 90 | 180 | 270;
  hiddenFromAudience?: boolean;
}

const STORAGE_KEY = 'eazix-wall-session-v1';
const PENDING_DIRECT_SHARE_KEY = 'eazix-wall-pending-direct-share-v1';

@Injectable({
  providedIn: 'root',
})
export class WallSessionService {
  /** קבצי ההזמנה הנוכחית שנפתחו לקיר האישי מדיאלוג סיכום */
  private wallSessionFiles: any[] | null = null;
  private wallSessionMeta: WallSessionMeta | null = null;
  /** One-shot share-from-orders → same UX as unshared "שתף". */
  private pendingDirectShare: WallPendingDirectShare | null = null;

  /** Emits whenever draft session files change (including hydrate / clear). */
  private readonly sessionFilesSubject: BehaviorSubject<any[]>;
  readonly sessionFiles$: Observable<any[]>;

  constructor() {
    this.hydrateFromStorage();
    this.hydratePendingDirectShare();
    this.sessionFilesSubject = new BehaviorSubject<any[]>(
      this.wallSessionFiles ? [...this.wallSessionFiles] : []
    );
    this.sessionFiles$ = this.sessionFilesSubject.asObservable();
  }

  /**
   * Open personal wall on a single page in the pending-share edit flow
   * (same as unshared tab "שתף"). Clears any draft multi-page session.
   */
  setPendingDirectShare(share: WallPendingDirectShare): void {
    const fileId = String(share?.fileId || '').trim();
    const page = Number(share?.page);
    const printingService =
      share?.printingService === 'express' ? 'express' : 'plotter';
    const serialName = String(share?.serialName || '').trim();
    const thumbUrl = String(share?.thumbUrl || '').trim();
    if (!fileId || !(page >= 1) || !serialName || !thumbUrl) {
      return;
    }
    const id =
      String(share?.id || '').trim() ||
      `${printingService}:${fileId}:${page}`;
    this.pendingDirectShare = {
      id,
      fileId,
      printingService,
      page,
      serialName,
      fileName: share?.fileName ? String(share.fileName) : '',
      thumbUrl,
      serverAddress: share?.serverAddress
        ? String(share.serverAddress)
        : '',
      orderId: share?.orderId ? String(share.orderId) : '',
    };
    this.persistPendingDirectShare();
    // Avoid showing the draft "which works to share?" picker.
    this.clearWallSessionFiles();
  }

  /** Peek without consuming (e.g. to skip draft hydrate). */
  getPendingDirectShare(): WallPendingDirectShare | null {
    if (!this.pendingDirectShare) {
      this.hydratePendingDirectShare();
    }
    return this.pendingDirectShare ? { ...this.pendingDirectShare } : null;
  }

  /** Take and clear the one-shot pending share intent. */
  consumePendingDirectShare(): WallPendingDirectShare | null {
    if (!this.pendingDirectShare) {
      this.hydratePendingDirectShare();
    }
    const next = this.pendingDirectShare;
    this.pendingDirectShare = null;
    this.removePendingDirectShareStorage();
    return next ? { ...next } : null;
  }

  setWallSessionFiles(files: any[], meta?: WallSessionMeta): void {
    // Shallow-safe copy of already-slim wall payloads (avoid cloning fat order graphs)
    this.wallSessionFiles = Array.isArray(files)
      ? files.map((file) => ({
          _id: file?._id || file?.fileId,
          fileId: file?._id || file?.fileId,
          originalName: file?.originalName || file?.name || file?.fileName || '',
          name: file?.name || file?.originalName || '',
          images: Array.isArray(file?.images)
            ? file.images.map((img: any, imgIndex: number) => ({
                page: Number(img?.page) >= 1 ? Number(img.page) : imgIndex + 1,
                wallThumbUrl: img?.wallThumbUrl || '',
                thumbnailPath: img?.thumbnailPath || '',
                path: img?.path || '',
                wallRotation: img?.wallRotation || 0,
                wallIncluded: !!img?.wallIncluded,
                wallTitle: img?.wallTitle != null ? String(img.wallTitle) : '',
                wallDescription:
                  img?.wallDescription != null
                    ? String(img.wallDescription)
                    : '',
              }))
            : [],
        }))
      : [];
    this.wallSessionMeta = meta ? { ...meta } : null;
    this.persistToStorage();
    this.sessionFilesSubject.next(this.getWallSessionFiles());
  }

  getWallSessionFiles(): any[] {
    if (!this.wallSessionFiles) {
      this.hydrateFromStorage();
    }
    return this.wallSessionFiles ? [...this.wallSessionFiles] : [];
  }

  getWallSessionMeta(): WallSessionMeta | null {
    if (!this.wallSessionMeta) {
      this.hydrateFromStorage();
    }
    return this.wallSessionMeta ? { ...this.wallSessionMeta } : null;
  }

  /**
   * Store draft rotation locally only (no API). Applied on publish.
   */
  setDraftPageRotation(
    fileId: string,
    page: number,
    rotation: 0 | 90 | 180 | 270
  ): void {
    this.patchDraftPages([{ fileId, page, rotation }]);
  }

  /** Persist include/title/description/rotation for draft pages (survive leave/return). */
  patchDraftPages(updates: WallSessionDraftPageState[]): void {
    if (!this.wallSessionFiles?.length || !updates?.length) {
      return;
    }
    let changed = false;
    for (const upd of updates) {
      const fileId = String(upd.fileId || '');
      const targetPage = Number(upd.page);
      if (!fileId || !(targetPage >= 1)) {
        continue;
      }
      for (const file of this.wallSessionFiles) {
        const id = String(file?._id || file?.fileId || '');
        if (id !== fileId) {
          continue;
        }
        const images = Array.isArray(file.images) ? file.images : [];
        images.forEach((image: any, imgIndex: number) => {
          const pageNum = Number(image?.page);
          const resolved =
            Number.isFinite(pageNum) && pageNum >= 1 ? pageNum : imgIndex + 1;
          if (resolved !== targetPage) {
            return;
          }
          if (!image || typeof image !== 'object') {
            file.images[imgIndex] = {
              page: resolved,
              wallRotation: upd.rotation || 0,
              wallIncluded: !!upd.included,
              wallTitle: upd.title || '',
              wallDescription: upd.description || '',
              wallHiddenFromAudience: !!upd.hiddenFromAudience,
            };
            changed = true;
            return;
          }
          if (upd.rotation != null && image.wallRotation !== upd.rotation) {
            image.wallRotation = upd.rotation;
            changed = true;
          }
          if (
            upd.included != null &&
            !!image.wallIncluded !== !!upd.included
          ) {
            image.wallIncluded = !!upd.included;
            changed = true;
          }
          if (upd.title != null && image.wallTitle !== upd.title) {
            image.wallTitle = String(upd.title);
            changed = true;
          }
          if (
            upd.description != null &&
            image.wallDescription !== upd.description
          ) {
            image.wallDescription = String(upd.description);
            changed = true;
          }
          if (
            upd.hiddenFromAudience != null &&
            !!image.wallHiddenFromAudience !== !!upd.hiddenFromAudience
          ) {
            image.wallHiddenFromAudience = !!upd.hiddenFromAudience;
            changed = true;
          }
        });
        file.images = images;
      }
    }
    if (changed) {
      this.persistToStorage();
    }
  }

  clearWallSessionFiles(): void {
    this.wallSessionFiles = null;
    this.wallSessionMeta = null;
    this.removeStorage();
    this.sessionFilesSubject.next([]);
  }

  private persistToStorage(): void {
    try {
      if (!this.wallSessionFiles?.length) {
        this.removeStorage();
        return;
      }
      const payload = JSON.stringify({
        files: this.wallSessionFiles,
        meta: this.wallSessionMeta,
      });
      // localStorage survives SPA leave/return and tab reloads better than sessionStorage.
      localStorage.setItem(STORAGE_KEY, payload);
      try {
        sessionStorage.setItem(STORAGE_KEY, payload);
      } catch {
        /* optional mirror */
      }
    } catch {
      /* ignore */
    }
  }

  private removeStorage(): void {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      /* ignore */
    }
  }

  private readStorageRaw(): string | null {
    try {
      const fromLocal = localStorage.getItem(STORAGE_KEY);
      if (fromLocal) {
        return fromLocal;
      }
    } catch {
      /* ignore */
    }
    try {
      return sessionStorage.getItem(STORAGE_KEY);
    } catch {
      return null;
    }
  }

  private hydrateFromStorage(): void {
    try {
      const raw = this.readStorageRaw();
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      const files = Array.isArray(parsed?.files) ? parsed.files : [];
      if (!files.length) {
        return;
      }
      this.wallSessionFiles = files;
      this.wallSessionMeta = parsed?.meta || null;
      // Migrate sessionStorage-only drafts into localStorage.
      this.persistToStorage();
    } catch {
      /* ignore */
    }
  }

  private persistPendingDirectShare(): void {
    try {
      if (!this.pendingDirectShare) {
        this.removePendingDirectShareStorage();
        return;
      }
      const payload = JSON.stringify(this.pendingDirectShare);
      sessionStorage.setItem(PENDING_DIRECT_SHARE_KEY, payload);
    } catch {
      /* ignore */
    }
  }

  private removePendingDirectShareStorage(): void {
    try {
      sessionStorage.removeItem(PENDING_DIRECT_SHARE_KEY);
    } catch {
      /* ignore */
    }
  }

  private hydratePendingDirectShare(): void {
    if (this.pendingDirectShare) {
      return;
    }
    try {
      const raw = sessionStorage.getItem(PENDING_DIRECT_SHARE_KEY);
      if (!raw) {
        return;
      }
      const parsed = JSON.parse(raw);
      const fileId = String(parsed?.fileId || '').trim();
      const page = Number(parsed?.page);
      const printingService =
        parsed?.printingService === 'express' ? 'express' : 'plotter';
      const serialName = String(parsed?.serialName || '').trim();
      const thumbUrl = String(parsed?.thumbUrl || '').trim();
      if (!fileId || !(page >= 1) || !serialName || !thumbUrl) {
        this.removePendingDirectShareStorage();
        return;
      }
      this.pendingDirectShare = {
        id:
          String(parsed?.id || '').trim() ||
          `${printingService}:${fileId}:${page}`,
        fileId,
        printingService,
        page,
        serialName,
        fileName: parsed?.fileName ? String(parsed.fileName) : '',
        thumbUrl,
        serverAddress: parsed?.serverAddress
          ? String(parsed.serverAddress)
          : '',
        orderId: parsed?.orderId ? String(parsed.orderId) : '',
      };
    } catch {
      this.removePendingDirectShareStorage();
    }
  }
}
